import { useCallback,useEffect, useMemo } from 'react';

import { GridValidRowModel } from '@mui/x-data-grid';
import { AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch } from 'app/hooks';
import { setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { RequestParamsBase } from 'common/services/api/Models/CommonRequestModels';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export function usePalmFetchEntities<TEntity extends GridValidRowModel, TThunkParams extends RequestParamsBase  | undefined>(
    entityName: string,
    thunkParams: TThunkParams,
    fetchEntitiesThunk: AsyncThunk<TEntity[], TThunkParams, Record<string, unknown>>,
    setProcessing: (processing: boolean) => void
) {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Memoize thunkParams to prevent infinite re-renders when callers pass inline objects
    const stableThunkParams = useMemo(() => thunkParams, [JSON.stringify(thunkParams)]);

    const fetchEntities = useCallback((signal?: AbortSignal) => {
        setProcessing(true);
        const params = {
            signal,
            ...stableThunkParams
        } as TThunkParams;
        dispatch(fetchEntitiesThunk(params as TThunkParams & undefined))
            .unwrap()
            .then((response) => {
                setProcessing(false);
                dispatch(setRows(response));
            })
            .catch((error) => {
                if (!signal?.aborted) {
                    setProcessing(false);
                    handleListFetchError(extractStatusCode(error.message), entityName, enqueueSnackbar);
                    dispatch(setRows([]));
                }
            });
    }, [dispatch, entityName, enqueueSnackbar, fetchEntitiesThunk, setProcessing, stableThunkParams]);

    useEffect(() => {
        const abortController = new AbortController();
        const { signal } = abortController;

        fetchEntities(signal);
        return () => {
            abortController.abort();
        };
    }, [fetchEntities]);

    return fetchEntities;

}
